import { NXMCall } from 'nexmo-client';
import { NexmoClient } from './nexmo-client';
import { WebRTCClient } from './webrtc-client';
import { IVoiceEvents } from '@/types';
import { Ringtone } from './ringtone';

interface VoiceSettings {
  readonly userId: string;
  readonly token: string;
  readonly webrtcDomain: string;
  readonly webrtcWSSAddress: string;
  readonly getGatewayURL: (key: string, accountId: string) => string;
  readonly providersAccountId: string;
  readonly isWebrtcEnabled: boolean;
  readonly isNexmoEnabled: boolean;
}

export class VoiceAPI {
  protected static isDebug = false;
  protected static webrtc?: typeof WebRTCClient;
  protected static nexmo?: NXMCall;
  protected static handlers: IVoiceEvents;
  /* Explicitly specify which API we are using for phone features */
  protected static mode: 'none' | 'webrtc' | 'nexmo' | 'softphone';
  protected static userId: string;
  public static applyCommand: (payload: any) => Promise<void>;

  static configure(args: {
    handlers: IVoiceEvents;
    applyCommand: (payload: any) => Promise<void>;
  }) {
    this.handlers = args.handlers;
    this.applyCommand = args.applyCommand;
  }

  /**
   * Nexmo checks token every ~3 hours and always should have up-to-date token.
   * WebRTC usually connects only once and needs a token on initialization.
   */
  static updateToken(token: string) {
    WebRTCClient.token = token;
    NexmoClient.authToken = token;
  }

  /** Log Voice API mode changes */
  static switch(mode: 'none' | 'webrtc' | 'nexmo' | 'softphone') {
    this.debug(`Set Mode - ${mode}`);
    this.mode = mode;
  }

  static async initialize(settings: VoiceSettings) {
    this.isDebug = JSON.parse(localStorage.getItem('HITL-Voice-Debug') || 'false');
    this.userId = settings.userId;
    this.debug('Initialize', this.webrtc, this.nexmo);

    if (settings.isWebrtcEnabled) {
      if (!this.webrtc) {
        WebRTCClient.wssAddress = settings.webrtcWSSAddress;
        WebRTCClient.domain = settings.webrtcDomain;
        WebRTCClient.userId = settings.userId;
        WebRTCClient.token = settings.token;

        WebRTCClient.handlers = this.getEventHandlersProxy('webrtc');
        this.webrtc = WebRTCClient;
        await WebRTCClient.initialize();
      } else {
        WebRTCClient.reconnect();
      }
    }

    if (settings.isNexmoEnabled) {
      if (!NexmoClient.client) {
        NexmoClient.gatewayUrl = settings.getGatewayURL;
        NexmoClient.providersAccountId = settings.providersAccountId;
        NexmoClient.authToken = settings.token;
        NexmoClient.handlers = this.getEventHandlersProxy('nexmo');
        await NexmoClient.initialize();
      }
    }
  }

  static async hangup(sessionId = '') {
    this.debug('Hanging Up...');

    switch (this.mode) {
      case 'nexmo':
        return await this.nexmo!.hangUp();
      case 'webrtc':
        return await this.webrtc!.hangup();
      case 'softphone':
        return await this.applyCommand({
          sessionId,
          command: 'remove_agent',
          data: {
            agentId: this.userId,
          },
          throwError: true,
        });
        return;
      default:
        return;
    }
  }

  static async answer() {
    this.debug('Answering...');

    switch (this.mode) {
      case 'nexmo':
        return await this.nexmo!.answer();
      case 'webrtc':
        return await this.webrtc!.answer();
      case 'softphone':
      default:
        return;
    }
  }

  static async mute() {
    this.debug('Mute');

    switch (this.mode) {
      case 'nexmo':
        return await this.nexmo!.conversation.me.mute(true);
      case 'webrtc':
        return this.webrtc!.mute();
      case 'softphone':
      default:
        return;
    }
  }

  static async unmute() {
    this.debug('Unmute');

    switch (this.mode) {
      case 'nexmo':
        return await this.nexmo!.conversation.me.mute(false);
      case 'webrtc':
        return this.webrtc!.unmute();
      case 'softphone':
      default:
        return;
    }
  }

  static async decline() {
    this.debug('Declining');
    switch (this.mode) {
      case 'nexmo':
        return await this.nexmo!.reject();
      case 'webrtc':
        return await this.webrtc!.decline();
      case 'softphone':
      default:
        return;
    }
  }

  static async dtmf(tone: string) {
    this.debug('Playing DTMF...');

    switch (this.mode) {
      case 'nexmo':
        Ringtone.play(`dtmf-${tone}`);
        return await this.nexmo!.conversation.media.sendDTMF(tone);
      case 'webrtc':
        Ringtone.play(`dtmf-${tone}`);
        return await this.webrtc!.dtmf(tone);
      case 'softphone':
      default:
        return;
    }
  }

  /**
   * Nexmo Api is returned trough a callback property,
   * while WebRTC is created directly.
   */
  protected static getEventHandlersProxy(
    type: 'webrtc' | 'nexmo'
  ): IVoiceEvents {
    const handlers = {
      ...this.handlers,
      onJoinCall: () => this.handlers.onJoinCall(),
      onLeaveCall: () => {
        this.switch('none');
        delete this.nexmo;
        this.handlers.onLeaveCall();
      },
    };

    if (type === 'nexmo') {
      return {
        ...handlers,
        onIncomingCall: (args: { number: string; call: NXMCall; }) => {
          this.switch('nexmo');
          this.nexmo = args.call;
          this.handlers.onIncomingCall({ number: args.number });
        },
      };
    }

    if (type === 'webrtc') {
      return {
        ...handlers,
        onIncomingCall: args => {
          this.switch('webrtc');
          this.handlers.onIncomingCall(args);
        },
      };
    }

    return this.handlers;
  }

  protected static debug(...args: any[]) {
    if (this.isDebug) {
      console.debug('[Voice API]', ...args);
    }
  }
}
